import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';

const AIProviderForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    platform: 'openai',
    display_name: '',
    api_key: '',
    organization_id: '',
    model: 'gpt-4o',
    is_active: true
  });

  const isEditing = Boolean(id);

  useEffect(() => {
    if (id) {
      fetchIntegration();
    }
  }, [id]);

  const fetchIntegration = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/integrations/${id}`);
      const data = response.data;
      setFormData({
        platform: data.platform,
        display_name: data.display_name,
        api_key: data.credentials?.api_key || '',
        organization_id: data.credentials?.organization_id || '',
        model: data.settings?.model || 'gpt-4o',
        is_active: data.is_active
      });
    } catch (error) {
      console.error('Error fetching integration:', error);
      setError('Failed to load AI provider');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const payload = {
        platform: formData.platform,
        display_name: formData.display_name,
        is_active: formData.is_active,
        credentials: {
          api_key: formData.api_key
        },
        settings: {
          model: formData.model
        }
      };

      if (formData.organization_id) {
        payload.credentials.organization_id = formData.organization_id;
      }

      if (isEditing) {
        await api.put(`/integrations/${id}`, payload);
      } else {
        await api.post('/integrations', payload);
      }
      
      navigate('/integrations');
    } catch (error) {
      console.error('Error saving integration:', error);
      setError(error.response?.data?.error || `Failed to ${isEditing ? 'update' : 'create'} AI provider`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (loading && isEditing) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button
          onClick={() => navigate('/integrations')}
          className="breadcrumb-link"
        >
          Integrations
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          {isEditing ? 'Edit AI Provider' : 'New AI Provider'}
        </span>
      </nav>

      {/* AI Provider Form Card */}
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              {isEditing ? 'Edit AI Provider' : 'Create New AI Provider'}
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              {isEditing ? 'Update AI provider configuration' : 'Add a new AI provider integration'}
            </p>
          </div>
        </div>

        {error && (
          <div style={{
            backgroundColor: 'var(--red-50)',
            border: '1px solid var(--red-200)',
            color: 'var(--red-800)',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          justifyContent: 'flex-end',
          marginBottom: '1.5rem',
          marginTop: '5px',
          paddingBottom: '1.5rem',
          borderBottom: '1px solid var(--gray-200)'
        }}>
          <button
            type="button"
            onClick={() => navigate('/integrations')}
            disabled={loading}
            className="btn btn-outline"
          >
            ✕ Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary"
            form="ai-provider-form"
            title={isEditing ? 'Update AI Provider' : 'Create AI Provider'}
          >
            {loading ? 'Saving...' : (isEditing ? '💾 Update' : 'Create AI Provider')}
          </button>
        </div>

        <form id="ai-provider-form" onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                Display Name *
              </label>
              <input
                type="text"
                name="display_name"
                value={formData.display_name}
                onChange={handleInputChange}
                required
                placeholder="e.g., Production OpenAI"
              />
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                Platform *
              </label>
              <select
                name="platform"
                value={formData.platform}
                onChange={handleInputChange}
                required
              >
                <option value="openai">OpenAI</option>
                <option value="anthropic">Anthropic</option>
                <option value="google_ai">Google AI</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                API Key *
              </label>
              <input
                type="password"
                name="api_key"
                value={formData.api_key}
                onChange={handleInputChange}
                required
                placeholder="Enter your API key"
              />
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                Organization ID (Optional)
              </label>
              <input
                type="text"
                name="organization_id"
                value={formData.organization_id}
                onChange={handleInputChange}
                placeholder="Enter organization ID"
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                AI Model *
              </label>
              <select
                name="model"
                value={formData.model}
                onChange={handleInputChange}
                required
              >
                {formData.platform === 'openai' && (
                  <>
                    <option value="o1">O1 (Latest)</option>
                    <option value="o1-preview">O1 Preview</option>
                    <option value="o1-mini">O1 Mini</option>
                    <option value="gpt-4o">GPT-4o</option>
                    <option value="gpt-4o-mini">GPT-4o Mini</option>
                    <option value="gpt-4-turbo">GPT-4 Turbo</option>
                    <option value="gpt-4">GPT-4</option>
                    <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                  </>
                )}
                {formData.platform === 'anthropic' && (
                  <>
                    <option value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet (Latest)</option>
                    <option value="claude-3-5-sonnet-20240620">Claude 3.5 Sonnet</option>
                    <option value="claude-3-5-haiku-20241022">Claude 3.5 Haiku</option>
                    <option value="claude-3-opus-20240229">Claude 3 Opus</option>
                    <option value="claude-3-sonnet-20240229">Claude 3 Sonnet</option>
                    <option value="claude-3-haiku-20240307">Claude 3 Haiku</option>
                  </>
                )}
                {formData.platform === 'google_ai' && (
                  <>
                    <option value="gemini-2.0-flash-exp">Gemini 2.0 Flash (Experimental)</option>
                    <option value="gemini-exp-1206">Gemini Experimental 1206</option>
                    <option value="gemini-1.5-pro">Gemini 1.5 Pro</option>
                    <option value="gemini-1.5-flash">Gemini 1.5 Flash</option>
                    <option value="gemini-pro">Gemini Pro</option>
                    <option value="gemini-pro-vision">Gemini Pro Vision</option>
                  </>
                )}
              </select>
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: '600', 
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                Status
              </label>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                height: '38px'
              }}>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  style={{ width: 'auto', cursor: 'pointer' }}
                />
                <span style={{ 
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem'
                }}>
                  Active
                </span>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AIProviderForm;
